import { Page, Div, Button, router, state, IRouteEvent } from '@codesuma/baseline'
import { PageHead } from '../../components/page-head'
import { ChoiceSlide, DateSlide, Slides, TitleSlide, WizardNav } from '../../components/wizard'
import { canAdvance, Draft, emptyDraft, nextLabel, reminderChoices, SlideId, step, visibleSlides, withoutStaleLead } from './flow'
import { WhenSlide } from './steps'
import calendar, { IDayCell } from '../../services/calendar'
import tasks from '../../services/tasks'
import { Reminder } from '../../services/occasion-repeat'
import reminders from '../../services/reminders'
import settings from '../../services/settings'
import { dayLine } from '../../helpers/day-line'
import { paleYear } from '../../helpers/year'
import style from './index.module.css'

// The task page (route /task?d=gy-gm-gd to make one, ?id=… to open one): the
// name, the day if it was not named, the hour if there is one, and how long
// before to be reminded. Reached from the day page — "+ کار" to add, tapping a
// task to change it — and from the month's own list, and saving returns to
// whichever it came from, where the list redraws.
//
// A route with no day in it is the month asking: there is no day to inherit
// there, so the wizard grows a step and asks for one, starting on today.
//
// It is the only place a task is edited. The row in the day's list carries a
// ring and a title and nothing else, so there is one place to look rather than
// a field in the row for the name and a panel under it for everything else.
//
// A task used to be typed straight into the day's list. It gained an hour, and
// an hour is a question with a follow-up — an hour's warning means nothing
// without one — so it now asks the way the occasion page does, a step at a
// time.
//
// One route for every day, like /day and /occasion: the date travels in the
// query, so the router caches a single page rather than one per date.

// Only the title step can take focus.
type WizardSlide = ReturnType<typeof Div> & { focus?: () => void }

export const TaskPage = () => {
    const base = Page()
    // Inline so it wins over baseline's default page background/padding: the
    // calendar's own paper and ink, so this reads as the same surface as the
    // day it came from. --gutter comes from the stylesheet, which also lets the
    // head pull back out of it, the way the occasion page does.
    base.style({
        background: 'var(--paper)',
        color: 'var(--ink)',
        padding: 'calc(var(--safe-top) + 1rem) var(--gutter) calc(var(--safe-bottom) + 1rem)',
    })
    base.addClass(style.base)

    const todayCell = () => {
        const t = calendar.today()
        return calendar.dayAt(t.gy, t.gm, t.gd, 0)
    }

    let day: IDayCell = todayCell()
    // Whether the flow carries a step for the day — see the route note above.
    let pickDay = false
    let draft: Draft = emptyDraft()
    let current: SlideId = 'title'
    let editing = '' // the id being changed, empty when making a new one

    // The app's one page head, with the line under it the editors carry.
    // Its date names the day being added to, so the page never loses its
    // context.
    const head = PageHead(undefined, { rule: true })
    base.append(head)

    const stage = Slides()
    const nav = WizardNav()
    base.append(stage, nav)

    // Only on an existing task, and only once confirmed: the first press arms
    // it, the second carries it out. Cheaper than a dialog, and it cannot be
    // triggered by a stray tap.
    const remove = Button()
    remove.addClass(style.remove)
    let armed = false
    const disarm = () => {
        armed = false
        remove.text('حذف کار')
        remove.removeClass(style.removeArmed)
    }
    remove.on('click', () => {
        if (!armed) {
            armed = true
            remove.text('برای حذف دوباره بزنید')
            remove.addClass(style.removeArmed)
            return
        }
        tasks.remove(editing)
        router.back()
    })
    base.append(remove)

    // The name is the whole of it — everything else carries a usable default,
    // which is what lets the shortcut below exist at all.
    const canSave = () => !!draft.title.trim()

    const renderNav = () => {
        const ids = visibleSlides(pickDay)
        const index = ids.indexOf(current)
        const last = index === ids.length - 1
        nav.setState({
            steps: ids.length,
            index,
            nextLabel: nextLabel(current, pickDay),
            nextEnabled: canAdvance(current, draft),
            showPrev: index > 0,
            // Not on the last step, where ادامه already is ذخیره.
            showDone: !last && canSave(),
        })
    }

    const buildSlide = (id: SlideId): WizardSlide => {
        switch (id) {
            case 'title': {
                const slide = TitleSlide(draft.title,
                    { heading: 'چه کاری؟', placeholder: 'مثلاً تماس با دکتر' })
                slide.on('text', (value: string) => {
                    draft.title = value
                    renderNav()
                })
                slide.on('enter', () => advance())
                return slide
            }
            case 'day': {
                const slide = DateSlide('چه روزی؟', day)
                slide.on('pick', (picked: IDayCell) => {
                    day = picked
                    syncDate()
                })
                return slide
            }
            case 'when': {
                const slide = WhenSlide(draft)
                // Clearing the hour can strand a lead that needed it, so the
                // draft is straightened here rather than at the save, where the
                // last step would already have been built from it.
                //
                // Assigned into the draft rather than replacing it: the slide
                // holds this object and writes the hour straight into it, so a
                // fresh one would leave it editing something nothing reads.
                slide.on('change', () => {
                    Object.assign(draft, withoutStaleLead(draft))
                    renderNav()
                })
                return slide
            }
            case 'reminder': {
                const slide = ChoiceSlide('یادآوری', reminderChoices(draft), draft.reminder)
                slide.on('pick', (value: Reminder) => {
                    draft.reminder = value
                    renderNav()
                    // The browser only grants this from a real gesture, and
                    // this tap is one. A no-op on native, which asks for
                    // itself when it has something to deliver.
                    if (value !== 'none') reminders.askWebPermission()
                })
                return slide
            }
        }
    }

    const goto = (id: SlideId, direction: 'forward' | 'back' | 'none') => {
        current = id
        const slide = buildSlide(id)
        stage.show(slide, direction)
        renderNav()
        disarm() // moving on withdraws an armed delete
        // After the slide has settled, so the keyboard does not open onto a
        // moving field.
        if (slide.focus) setTimeout(() => slide.focus!(), 220)
    }

    // Writing it down and leaving. Reached two ways: off the end of the flow,
    // and off the shortcut under the controls — every step after the name only
    // adds to what is already saveable, so there is no reason to make someone
    // walk past questions they have no answer for.
    const commit = () => {
        const saved = withoutStaleLead(draft)
        // An emptied hour is stored as absent rather than as an empty string,
        // so every reader can ask whether the task has one the same way.
        const patch = { title: saved.title.trim(), time: saved.time || undefined, reminder: saved.reminder }
        if (editing) tasks.update(editing, patch)
        else tasks.add(day, patch)
        router.back()
    }

    const advance = () => {
        if (!canAdvance(current, draft)) return
        const next = step(current, pickDay, 1)
        if (next) return goto(next, 'forward')
        commit()
    }

    nav.on('next', advance)
    // Guarded as well as hidden: the shortcut is only drawn once there is a
    // name, and a name is the one thing a task cannot be saved without.
    nav.on('done', () => {
        if (canSave()) commit()
    })
    nav.on('prev', () => {
        const previous = step(current, pickDay, -1)
        if (previous) goto(previous, 'back')
    })

    // Every entry starts over — the router keeps this page alive between
    // visits, so neither the draft nor the day may carry over.
    base.on('enter', ({ query }: IRouteEvent) => {
        const q = (query || {}) as Record<string, string>
        const existing = q.id ? tasks.byId(q.id) : undefined

        if (existing) {
            editing = existing.id
            pickDay = false
            // The day is the one it was made for, not anything in the query.
            day = calendar.dayAt(existing.gy, existing.gm, existing.gd, 0)
            draft = { title: existing.title, time: existing.time || '', reminder: existing.reminder }
        } else {
            editing = ''
            const parts = String(q.d || '').split('-').map(Number)
            const named = parts.length === 3 && parts.every(n => Number.isFinite(n) && n > 0)
            day = named ? calendar.dayAt(parts[0], parts[1], parts[2], 0) : todayCell()
            // Nobody named a day, so the wizard asks for one.
            pickDay = !named
            draft = emptyDraft()
        }

        head.setTitle(editing ? 'ویرایش کار' : 'کار تازه')
        remove.style({ display: editing ? '' : 'none' })
        disarm()
        syncDate()
        goto(visibleSlides(pickDay)[0], 'none')
    })

    // The label follows a moon-sighting correction and a change of primary
    // calendar, the same way the occasion page's does.
    function syncDate() {
        head.setDate(paleYear(dayLine(calendar.dayAt(day.gy, day.gm, day.gd, 0), settings.get().system)))
    }
    state.on('hijri', syncDate)
    state.on('settings', syncDate)
    base.on('unmounted', () => {
        state.off('hijri', syncDate)
        state.off('settings', syncDate)
    })

    return base
}
